import { useEffect, useState } from "react";
import axios from "axios";
import { API_URL } from "../../../../constants";
import { Check } from "lucide-react";
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, Chip, CircularProgress, Grid, Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "../../../../language";

const CATEGORY_DISPLAY_NAMES = {
  butyrateProducingBacteria: "Butyrate-producing Bacteria",
  mucinProducingBacteria:    "Mucin-producing Bacteria",
  probioticBacteria:         "Probiotic Bacteria",
  sulfateReducingBacteria:   "Sulphate-reducing Bacteria",
  fbratio:                   "F/B Ratio",
};

function evalColor(evaluation, theme) {
  if (evaluation === "H" || evaluation === "L") return theme.palette.traffic.orange;
  return theme.palette.traffic.green;
}

function evalLabel(evaluation, t) {
  const key = evaluation === "H" ? "bt_status_high" : evaluation === "L" ? "bt_status_low" : null;
  if (!key) return "";
  return t(key) !== key ? t(key) : evaluation;
}

export default function GutBacteriaCategory({ date }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const dark = theme.palette.mode === "dark";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/api/gut/bacteria-records/?date=${date}`)
      .then(res => setRecords(res.data))
      .catch(err => console.error("Fetch error:", err))
      .finally(() => setLoading(false));
  }, [date]);

  const grouped = records.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  if (loading) {
    return <Box textAlign="center" my={4}><CircularProgress size={28} /></Box>;
  }

  // Same glass-card treatment as the blood marker subsystem cards — rounded,
  // blurred, no colored left border stripe.
  const glassSx = {
    borderRadius: "16px",
    overflow: "hidden",
    bgcolor: dark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
    backdropFilter: "blur(28px) saturate(180%)",
    WebkitBackdropFilter: "blur(28px) saturate(180%)",
    border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.35)"}`,
    boxShadow: dark
      ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
      : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
  };

  return (
    <Grid container>
      {Object.entries(grouped)
        .filter(([category]) => category !== "all_genus")
        .map(([category, items]) => {
          const outOfRangeCount = items.filter(i => i.evaluation === "H" || i.evaluation === "L").length;
          const label = CATEGORY_DISPLAY_NAMES[category] || category;

          return (
            <Grid key={category} size={12} sx={{ mb: 2 }}>
              <Box sx={glassSx}>
                <Accordion
                  defaultExpanded={false}
                  disableGutters
                  elevation={0}
                  square
                  sx={{ bgcolor: "transparent", boxShadow: "none", "&:before": { display: "none" } }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      px: 2.5, minHeight: 44,
                      "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1.25 },
                      "& .MuiAccordionSummary-expandIconWrapper": { ml: 1.25 },
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: 0.2 }}>
                      {label}
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    {outOfRangeCount > 0 ? (
                      <Tooltip title={t("bt_tooltip_out_of_range")} placement="top">
                        <Chip
                          label={`${outOfRangeCount}`}
                          size="small"
                          sx={{
                            bgcolor: "transparent",
                            color: theme.palette.traffic.orange,
                            border: "1px solid", borderColor: theme.palette.traffic.orange,
                            fontWeight: 500, fontSize: "0.8rem",
                            width: 22, height: 22,
                            borderRadius: "999px",
                            "& .MuiChip-label": { px: 0, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" },
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("bt_tooltip_all_in_range")} placement="top">
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Check size={18} strokeWidth={2.5} color={theme.palette.traffic.green} />
                        </Box>
                      </Tooltip>
                    )}
                  </AccordionSummary>

                  <AccordionDetails sx={{ p: 0 }}>
                    {items.map((item, i) => (
                      <Box key={item.id} sx={{
                        bgcolor: i % 2 === 0
                          ? (theme.palette.mode === "light" ? "grey.C50"  : "greydark.C900")
                          : (theme.palette.mode === "light" ? "grey.C100" : "greydark.C800"),
                      }}>
                        <Box sx={{ px: 2.5, py: 0.75 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: evalColor(item.evaluation, theme) }} />
                            <Typography variant="body2" fontWeight={500}>
                              {item.species || item.phylum}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", pl: "16px", mt: 0.75 }}>
                            <Typography variant="body1" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {Number(item.frequency).toFixed(2)} %
                            </Typography>
                            <Box sx={{ flexGrow: 1 }} />
                            {item.evaluation === "N"
                              ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                              : <Typography variant="subtitle2" fontWeight={700} sx={{ color: evalColor(item.evaluation, theme) }}>
                                  {evalLabel(item.evaluation, t)}
                                </Typography>
                            }
                          </Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: "block", pl: "16px", mt: 0.5 }}>
                            Ref: {item.referenceMin} – {item.referenceMax}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grid>
          );
        })}
    </Grid>
  );
}
